/**
 * `/admin/automation` — the coding-account overview (A7Y-3802).
 *
 * One row per account, answering the question an operator arrives with: which
 * account has room right now. Everything else an account carries — the full
 * capacity block, the reserved-capacity callout, the raw policy string, the
 * refresh-failure message and the role explanation — lives on the per-account
 * detail page at `/admin/automation/quota/:account`, which reads from these
 * same two cached queries and issues no request of its own.
 *
 * The Refresh button and every refresh state (queued, pending, timed out,
 * unavailable-with-reason, error) stay here, unchanged: they act on the fleet,
 * not on one account.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { AiQuotaAccountStatus } from '@a7y/types';
import { api } from '@/lib/api';
import { ProviderIcon, QuotaBar, RoleBadge, ScopedLimits } from './quota-account-parts';
import { hasCachedValues, quotaAccountDetailPath, sourceLabel } from './quota-account-meta';
import { formatQuotaAgeInterval } from './quota-display';
import { fmtLocal, useNow } from './shared';

/**
 * How many grey placeholder rows to show while the quota query is in flight.
 *
 * A7Y-3787: this is a LAYOUT number and deliberately NOT the size of the
 * account fleet. It read as a fleet count until claude03 was registered and
 * made it wrong, which is the stale-literal class that ticket exists to remove.
 * There is nothing here to derive it from honestly: the rendered rows come from
 * `quota.data.accounts`, which the API builds from `CLAUDE_ACCOUNT_REGISTRY`,
 * and that array is by definition absent in the branch this constant serves.
 * `AiQuotaAccountKey` cannot stand in either — it is a type, erased at runtime.
 * A hand-kept count here would be a second source of truth beside the registry.
 * A7Y-3797 covers deriving the union itself from the registry; a runtime
 * account list falls out of that, and this can read it.
 */
const SKELETON_PLACEHOLDER_ROWS = 4;

const REFRESH_TIMEOUT_MS = 2 * 60_000;

/** Marks a row whose latest refresh failed, or that has never reported. The
 *  message itself lives on the detail page — the glyph is the pointer to it,
 *  which is what keeps the row a row. */
function RowAlert({ account }: { account: AiQuotaAccountStatus }) {
  const neverReported = !hasCachedValues(account);
  if (!account.error && !neverReported) return null;
  const title = account.error
    ? `Latest refresh failed: ${account.error}`
    : 'Awaiting the first quota refresh.';
  return (
    <span
      className="inline-flex shrink-0 items-center text-amber-500"
      title={title}
      aria-label={title}
      role="img"
      data-testid={`quota-account-alert-${account.account}`}
    >
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  );
}

function AccountRow({
  account,
  eligible,
  now,
}: {
  account: AiQuotaAccountStatus;
  eligible: boolean;
  now: number;
}) {
  const navigate = useNavigate();
  const cached = hasCachedValues(account);
  const displayedAt = cached ? account.lastSuccessfulAt : account.updatedAt;
  const source = cached ? sourceLabel(account) : null;
  const detailPath = quotaAccountDetailPath(account.account);

  return (
    <tr
      onClick={() => navigate(detailPath)}
      className={`cursor-pointer align-middle ${
        eligible
          ? 'bg-emerald-50/60 dark:bg-emerald-950/20'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-900'
      }`}
      title={
        eligible ? 'The account the most recent quota waterfall chose to IMPLEMENT on' : undefined
      }
      data-testid={`quota-account-row-${account.account}`}
    >
      <td className="w-px px-3 py-2">
        <ProviderIcon provider={account.provider} />
      </td>
      {/* Role leads, and `w-px` is what makes it narrow: in an auto-layout
          table that collapses the column onto its own content — the badge —
          instead of letting it take a share of the table's slack. */}
      <td className="w-px px-3 py-2">
        <RoleBadge role={account.role} />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          {/* A real link, not just the row handler: middle-click, keyboard
              focus and "copy link address" all have to keep working. The
              mailbox is shown whole — it is the row's identity, and truncating
              it made the three tpf@ and claude0N@ accounts read alike. This is
              the column that absorbs the table's spare width, which is why
              every other one is `w-px`. */}
          <Link
            to={detailPath}
            onClick={(event) => event.stopPropagation()}
            className="whitespace-nowrap text-sm font-semibold text-zinc-900 hover:underline dark:text-zinc-100"
          >
            {account.email ?? account.label}
          </Link>
          <RowAlert account={account} />
        </div>
      </td>
      <td className="w-32 px-3 py-2">
        <QuotaBar
          label=""
          value={cached ? account.session : null}
          thresholds={account.thresholds}
          now={now}
          reset="interval"
        />
      </td>
      <td className="w-32 px-3 py-2">
        <QuotaBar
          label=""
          value={cached ? account.weekly : null}
          thresholds={account.thresholds}
          now={now}
          reset="interval"
        />
        {/* Beneath the weekly bar because these ARE weekly windows — but never
            folded into its percentage: a model at 100% means that model is
            unavailable until the window cycles, not that the account is. */}
        {cached ? <ScopedLimits limits={account.scopedLimits} now={now} /> : null}
      </td>
      {/* The interval alone. Which reading it measures — a cached success or a
          failed attempt — and its absolute time and source are the tooltip's
          job; the row's amber glyph is what marks a failed one, so the cell
          does not need to spend words on it. */}
      <td className="w-px whitespace-nowrap px-3 py-2 text-[11px] text-zinc-400">
        {displayedAt ? (
          <span
            title={`${cached ? 'Updated' : 'Last attempt'} ${fmtLocal(displayedAt)}${
              source ? ` via ${source}` : ''
            }`}
          >
            {formatQuotaAgeInterval(displayedAt, now) ?? fmtLocal(displayedAt)}
          </span>
        ) : (
          <span className="text-zinc-400">Never reported</span>
        )}
      </td>
    </tr>
  );
}

export function QuotaStatusSection() {
  const queryClient = useQueryClient();
  const now = useNow(60_000);
  const [requestedAt, setRequestedAt] = useState<string | null>(null);
  const [refreshTimedOut, setRefreshTimedOut] = useState(false);
  const quota = useQuery({
    queryKey: ['autobuild-quota'],
    queryFn: () => api.getAiQuotaStatus(),
    refetchInterval: requestedAt ? 5_000 : 30_000,
    refetchOnWindowFocus: false,
  });
  // Quota rows come from the flight recorder (BigQuery). The lease endpoint
  // combines its independently-read Firestore reservations with the newest
  // cached quota attempt for the server-side capacity verdict, and the detail
  // page reads it from this same cache. The two requests remain separate so
  // either surface can still render when the other source fails.
  const leases = useQuery({
    queryKey: ['autobuild-leases'],
    queryFn: () => api.getAutomationLeaseStatus(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
  const refresh = useMutation({
    mutationFn: () => api.refreshAiQuotaStatus(),
    onSuccess: (response) => {
      setRefreshTimedOut(false);
      setRequestedAt(response.requestedAt);
      void queryClient.invalidateQueries({ queryKey: ['autobuild-quota'] });
    },
  });

  useEffect(() => {
    if (!requestedAt || !quota.data) return;
    const requestedMs = Date.parse(requestedAt);
    const allAttempted = quota.data.accounts.every(
      (account) => account.updatedAt && Date.parse(account.updatedAt) >= requestedMs
    );
    // Clearing this here is what cancels the sibling 30s timeout effect below,
    // which keys off `requestedAt`. Deriving "settled" during render instead
    // would leave `requestedAt` set, so that timer would keep running and flip
    // refreshTimedOut on a refresh that in fact succeeded.
    if (allAttempted) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRequestedAt(null);
      // The lease endpoint combines these new readings with live reservations;
      // do not leave its capacity verdict waiting for the next 30-second poll.
      void queryClient.invalidateQueries({ queryKey: ['autobuild-leases'] });
    }
  }, [queryClient, quota.data, requestedAt]);

  useEffect(() => {
    if (!requestedAt) return;
    const timer = window.setTimeout(() => {
      setRequestedAt(null);
      setRefreshTimedOut(true);
    }, REFRESH_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [requestedAt]);

  // Touched only so a lease failure is not silent on the overview. The numbers
  // themselves live on the detail page; this line says the capacity half of
  // that page is currently unreadable rather than empty.
  const leaseUnreadable = leases.isError;

  const isRefreshPending = refresh.isPending || requestedAt != null;
  const isRefreshUnavailable = quota.data?.manualRefresh?.available === false;
  const refreshUnavailableReason = isRefreshUnavailable
    ? (quota.data?.manualRefresh?.unavailableReason ?? 'Manual refresh is unavailable.')
    : null;
  const refreshError = refresh.error instanceof Error ? refresh.error.message : null;

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
            Coding account quotas
          </h2>
          {/* One line. Accounts hold roles as well as quota, and what each role
              means is the badge's tooltip and the detail page's prose — this
              sentence no longer restates either. */}
          <p className="mt-0.5 text-xs text-zinc-500">
            Cached session and weekly usage per account — open a row for capacity, reset times and
            refresh errors. A green row is the account the last waterfall implemented on.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={isRefreshPending || isRefreshUnavailable}
          title={refreshUnavailableReason ?? undefined}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-600 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshPending ? 'animate-spin' : ''}`} />
          {requestedAt ? 'Refresh queued…' : refresh.isPending ? 'Requesting…' : 'Refresh'}
        </button>
      </div>

      {quota.isError ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Quota status failed to load. Check the Worker logs for /admin/autobuild/quota.
        </div>
      ) : null}

      {refresh.isError && !isRefreshUnavailable ? (
        <div className="text-xs text-red-600 dark:text-red-400">
          Refresh could not be queued
          {refreshError ? `: ${refreshError}` : '.'}
        </div>
      ) : null}

      {refreshUnavailableReason ? (
        <div className="text-xs text-amber-700 dark:text-amber-400">{refreshUnavailableReason}</div>
      ) : null}

      {refreshTimedOut ? (
        <div className="text-xs text-amber-700 dark:text-amber-400">
          The refresh is taking longer than expected. Cached values will update when the workflow
          completes.
        </div>
      ) : null}

      {leaseUnreadable ? (
        <div className="text-xs text-amber-700 dark:text-amber-400">
          Lease capacity could not be read, so the ceilings and reservations on the account pages
          are unknown.
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs text-zinc-500 dark:bg-zinc-900">
            <tr>
              {/* `w-px` here for the same reason as Role/Resets/Updated: in an
                  auto-layout table an unconstrained column takes a share of the
                  slack, and this one holds a 16px glyph. Leaving it free made it
                  as wide as the mailbox column it sits next to. */}
              <th className="w-px px-3 py-2 font-medium">
                <span className="sr-only">Service</span>
              </th>
              <th className="w-px px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Account</th>
              <th className="px-3 py-2 font-medium">Session</th>
              <th className="px-3 py-2 font-medium">Weekly</th>
              <th className="w-px px-3 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white dark:divide-zinc-800 dark:bg-zinc-950">
            {quota.isLoading
              ? Array.from({ length: SKELETON_PLACEHOLDER_ROWS }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan={6} className="px-3 py-3">
                      <div className="h-4 animate-pulse rounded bg-zinc-100 dark:bg-zinc-900" />
                    </td>
                  </tr>
                ))
              : (quota.data?.accounts ?? []).map((account) => (
                  <AccountRow
                    key={account.account}
                    account={account}
                    eligible={quota.data?.eligibleAccount === account.account}
                    now={now}
                  />
                ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
